// @flow strict
import type { StrandExpandedState } from './SkillsPage/skills-page.hooks.js';

import * as React from 'react';
import { find, propEq } from 'ramda';
import styles from './achievement-level-panel.css';
import Popover from './components/popover.jsx';
import infoSvg from './images/ic_info_24px.svg';
import iconStyles from './icons.css';
import svgImageFn from './svg-image.jsx';
import { groupSkillsByStrand, toKebabCase } from './strand-utils.js';
import StrandGrouping from './strand-grouping.jsx';
import type { AchievementLevel, Strand } from './ald-types'

const InfoIcon = svgImageFn(
  iconStyles.mediumSquare,
  iconStyles.darkIcon,
  infoSvg
);

type StrandGroupingPlaceholderProps = {
  borderColor: string,
  dimmedStyle: string,
  nextStrand: string | Strand,
  strand: Strand
}

const StrandGroupingPlaceholder = ({
  borderColor,
  dimmedStyle,
  nextStrand,
  strand,
}:StrandGroupingPlaceholderProps): React.Node => {
  return (
    <div
      aria-hidden="true"
      style={{
        gridRow: `${toKebabCase(strand)} / span ${toKebabCase(nextStrand)}`,
        borderColor: borderColor
      }}
      className={`${styles.sideBorder} ${dimmedStyle}`}
    ></div>
  );
};

export type Props = {|
  achievementLevel: AchievementLevel,
  changeStrandDisplayStatus: (string) => void,
  dimmed: boolean,
  isFirstPanel: boolean,
  nextAchievementLevelName: string,
  numPanels: number,
  panelNum: number,
  strandDisplayStatus: StrandExpandedState,
  strandIndicatorGroups: Array<[Strand, Array<string>]>,
|};

type InfoTextType = { currentAchievementLevelIndex: number,
  currentAchievementLevelName: string,
  nextAchievementLevelName: string,
  numberOfAchievementLevels: number
};

const InfoText = ({
  currentAchievementLevelName,
  currentAchievementLevelIndex,
  numberOfAchievementLevels,
  nextAchievementLevelName,
}:InfoTextType): React.Node => {
  if (currentAchievementLevelIndex === 0) {
    return (
      <p>
        Students currently at the{' '}
        <em
          className={styles.emphasize}
          data-cy={`${toKebabCase(currentAchievementLevelName)}-info-text-bold1`}
        >
          {currentAchievementLevelName}
        </em>{' '}
        level should access grade-level content as well as review content from the prior grade(s).
      </p>
    )
  } else if (currentAchievementLevelIndex + 1 === numberOfAchievementLevels) {
    return (
      <p>
        Students currently at the{' '}
        <em
          className={styles.emphasize}
          data-cy={`${toKebabCase(currentAchievementLevelName)}-info-text-bold1`}
        >
          {currentAchievementLevelName}
          </em>{' '}
        level should start learning content at the next grade level(s).
      </p>
    )
  } else {
    return (
      <p>
        Students currently at the{' '}
        <em
          className={styles.emphasize}
          data-cy={`${toKebabCase(currentAchievementLevelName)}-info-text-bold1`}
        >
          {currentAchievementLevelName}
        </em>{' '}
        level should start learning content at the{' '}
        <em
          className={styles.emphasize}
          data-cy={`${toKebabCase(currentAchievementLevelName)}-info-text-bold2`}
        >
          {nextAchievementLevelName}
        </em>{' '}
        level as well as continue learning content at this level.
      </p>
    )
  }
}

const AchievementLevelPanel: React.AbstractComponent<Props> = ({
  achievementLevel,
  changeStrandDisplayStatus,
  dimmed,
  isFirstPanel,
  nextAchievementLevelName,
  numPanels,
  panelNum,
  strandDisplayStatus,
  strandIndicatorGroups,
}: Props) => {
  const achievementLevelName = achievementLevel.achievementLevelConfig.name;
  const borderColor = achievementLevel.achievementLevelConfig.color;
  const dimmedStyle = dimmed ? styles.dim : '';

  const skillsGroupedByStrand = groupSkillsByStrand(achievementLevel.skills);

  const achievementLevelContainerCyValue = toKebabCase(
    `achievement-level-${achievementLevelName}`
  );
  const achievementLevelHeadingCyValue = toKebabCase(
    `achievement-name-${achievementLevelName}`
  );

  const jsxFragment = (
    <div
      aria-hidden={dimmed}
      aria-label={`Panel ${
        panelNum + 1
      } of ${numPanels} ${achievementLevelName}`}
      aria-live={`${isFirstPanel ? 'polite' : 'off'}`}
      className={`${styles.panelUnorderedList} ${styles.displayContents}`}
      role="group"
    >
      <div
        style={{borderColor: borderColor, backgroundColor:achievementLevel.achievementLevelConfig.color}}
        className={`${styles.achievementLevelName} ${dimmedStyle}`}
        data-cy={`${achievementLevelContainerCyValue}`}
      >
        <h2
          className="nwea-ds-v1-typography-heading-5"
          data-cy={achievementLevelHeadingCyValue}
        >
          {achievementLevelName}
        </h2>
        {
          achievementLevel.achievementLevelConfig.popoverContent
            ? <Popover
                popoverContent={achievementLevel.achievementLevelConfig.popoverContent}
                popoverId={toKebabCase(achievementLevelName)}
                dimmed={dimmed}
                ariaLabel="info"
              >
                <InfoIcon />
              </Popover>
            : null
        }
      </div>
      <div
        style={{borderColor: borderColor}}
        className={`${styles.sideBorder} ${styles.infoPanel} ${dimmedStyle}`}
        aria-hidden={dimmed}
        data-cy={`${achievementLevelContainerCyValue}-info-text`}
      >
        <InfoText
          currentAchievementLevelIndex={panelNum}
          currentAchievementLevelName={achievementLevel.achievementLevelConfig.name}
          nextAchievementLevelName={nextAchievementLevelName}
          numberOfAchievementLevels={numPanels}
        />
      </div>
      {strandIndicatorGroups.map(
        ([strandRowName, indicatorRowNames], index) => {
          const strandTuple = find(
            propEq(0, strandRowName),
            skillsGroupedByStrand
          );
          const nextStrand =
            index === strandIndicatorGroups.length - 1
              ? 'footer'
              : strandIndicatorGroups[index + 1][0];

          if (strandTuple) {
            const [strand, specificSkills] = strandTuple;
            return (
              <StrandGrouping
                achievementLevel={achievementLevel.achievementLevelConfig}
                changeStrandDisplayStatus={changeStrandDisplayStatus}
                dimmed={dimmed}
                key={strand}
                nextStrand={nextStrand}
                indicatorRowNames={indicatorRowNames}
                skillsVisible={strandDisplayStatus[strand] === 'expanded'}
                specificSkills={specificSkills}
                strand={strand}
              />
            );
          } else {
            // this achievement level doesn't have this strand, this placeholder will fill out the grid so that the borders will display without gaps
            return (
              <StrandGroupingPlaceholder
                borderColor={borderColor}
                dimmedStyle={dimmedStyle}
                key={strandRowName}
                nextStrand={nextStrand}
                strand={strandRowName}
              />
            );
          }
        }
      )}
      <div
        style={{borderColor: borderColor}}
        className={`${styles.achievementLevelFooter} ${dimmedStyle}`}
        aria-hidden="true"
      ></div>
    </div>
  );
  return jsxFragment;
};

export default AchievementLevelPanel;
